/**
 * product-specs-search.js
 * Recherche dans le bloc caractéristiques de la fiche produit (accordéon cartes).
 *
 * Markup consommé (snippets/custom-informations.liquid, section .product-specs) :
 * - `data-specs-root` : racine, `data-specs-search-input`, `data-specs-search-clear`
 * - `data-specs-count` (+ `data-specs-count-template`), `data-specs-empty`
 * - `.product-specs__cat` (details, libellé dans `data-cta-name`) > `.product-specs__list`
 *   > `.product-specs__row` > `.product-specs__label` + `.product-specs__value`
 *
 * 🔴 AUCUNE manipulation du DOM au chargement : l'état initial (catégorie « Général »
 * ouverte, autres fermées) est correct côté serveur — toute écriture ici casserait le CLS.
 * L'index est construit PARESSEUSEMENT, à la première frappe.
 *
 * Moteur volontairement plus léger que celui de la FAQ (product-faq.js : racinisation,
 * synonymes métier, score par champ, tri de pertinence). Le corpus est ici fait de
 * libellés et de valeurs courts et techniques (« Poids du vélo », « Bosch Performance CX »,
 * « 625 Wh ») : un ET sur des sous-chaînes suffit, il donne la recherche pendant la frappe
 * (« bat » → « batterie ») sans table de synonymes, et l'ordre des lignes reste celui du
 * rendu (ordre technique voulu, jamais réordonné par pertinence).
 *
 * ⚠ Les <details> ouverts par la recherche émettent un `toggle`, écouté en capture par
 * tracking-ecommerce.liquid (`spec_action`). Sans le mutisme par `.is-searching` posé ici,
 * une seule frappe pousserait jusqu'à 9 events `click_cta` que l'utilisateur n'a pas joués.
 *
 * Modèle : product-faq.js (IIFE + 'use strict' + pushEvent + garde document.readyState).
 */
(function () {
  'use strict';

  var FILTER_DEBOUNCE_MS = 200;
  var TRACK_DEBOUNCE_MS = 800;
  var TRACK_MIN_LENGTH = 3;
  var MIN_TERM_LENGTH = 2;
  var FIRST_CLASS = 'product-specs__row--first-visible';
  var SECOND_CLASS = 'product-specs__row--second-visible';

  // Mots vides : les libellés du corpus sont de la forme « Poids du vélo », « Type de
  // freins » — sans ce filtre, les articles que porte la moitié des lignes deviendraient
  // bloquants dans le ET ci-dessous.
  var STOPWORDS = {};
  'de du des la le les et en un une au aux pour par avec sur'.split(' ').forEach(function (w) {
    STOPWORDS[w] = true;
  });

  function pushEvent(name, data) {
    if (typeof window.trackEvent === 'function') {
      window.trackEvent(name, data);
    } else {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: name }, data));
    }
  }

  function debugWarn() {
    // `window.DEBUG_MODE` n'existe pas — tracking-core.liquid déclare `const DEBUG_MODE`
    // en scope lexical de script, pas une propriété window (cf. product-faq.js).
    if (typeof DEBUG_MODE !== 'undefined' && DEBUG_MODE) {
      console.warn.apply(console, ['[product-specs-search]'].concat(Array.prototype.slice.call(arguments)));
    }
  }

  // Bloc `ecommerce` standard de la fiche produit (tracking-ecommerce.liquid) : sans lui,
  // les events de la recherche ne se joignent pas au funnel GA4. Renvoie `undefined` hors
  // fiche produit — la clé disparaît simplement du payload.
  function productEcommerce() {
    if (typeof window.mbProductEcommerce !== 'function') return undefined;
    try {
      return window.mbProductEcommerce();
    } catch (e) {
      debugWarn('mbProductEcommerce a échoué — event émis sans bloc ecommerce.');
      return undefined;
    }
  }

  // Normalisation d'accents locale (aucune fonction globale réutilisable dans le thème) :
  // même pattern que product-faq.js (toLowerCase + NFD + strip des combinantes).
  function normalize(str) {
    var s = String(str || '').toLowerCase();
    try {
      s = s.normalize('NFD').replace(/[̀-ͯ]/g, '');
    } catch (e) {}
    return s;
  }

  // Termes de la requête. La comparaison se fait en SOUS-CHAÎNE, ce qui rattrape déjà le
  // singulier tapé pour un pluriel indexé (« frein » → « freins ») ; l'inverse demande une
  // variante dépluralisée, sans quoi « freins » ne trouverait pas « Frein avant ».
  function queryTerms(raw) {
    var words = normalize(raw)
      .replace(/['’]/g, ' ')
      .split(/[^a-z0-9]+/);
    var seen = {};
    var terms = [];
    for (var i = 0; i < words.length; i++) {
      var w = words[i];
      if (!w || w.length < MIN_TERM_LENGTH || STOPWORDS[w] || seen[w]) continue;
      seen[w] = true;
      var singular = '';
      if (w.length >= 4 && (w.charAt(w.length - 1) === 's' || w.charAt(w.length - 1) === 'x')) {
        singular = w.slice(0, -1);
      }
      terms.push({ w: w, singular: singular });
    }
    return terms;
  }

  function hayMatches(hay, term) {
    if (hay.indexOf(term.w) !== -1) return true;
    return term.singular !== '' && hay.indexOf(term.singular) !== -1;
  }

  function init() {
    var root = document.querySelector('[data-specs-root]');
    if (!root) return;
    var searchInput = root.querySelector('[data-specs-search-input]');
    if (!searchInput) return;

    var clearBtn = root.querySelector('[data-specs-search-clear]');
    var countEl = root.querySelector('[data-specs-count]');
    var emptyEl = root.querySelector('[data-specs-empty]');
    var countTemplate = countEl ? countEl.getAttribute('data-specs-count-template') : '';
    var cats = root.querySelectorAll('.product-specs__cat');

    if (countEl && !countTemplate) {
      debugWarn('data-specs-count-template absent sur [data-specs-count] — compteur en repli brut.');
    }

    var index = null;
    var catState = null;
    var isSearching = false;
    var muteTimer = null;
    var lastCount = 0;

    // Construit l'index à la première frappe seulement.
    function ensureIndex() {
      if (index) return;
      index = [];
      for (var c = 0; c < cats.length; c++) {
        // Libellé de catégorie indexé sur chacune de ses lignes : « transmission » ou
        // « freins » remonte alors toute la carte, alors que le mot n'apparaît pas dans
        // le libellé de chaque ligne. `data-cta-name` porte déjà ce libellé (tracking).
        var catLabel = normalize(cats[c].getAttribute('data-cta-name') || '');
        var rows = cats[c].querySelectorAll('.product-specs__row');
        for (var r = 0; r < rows.length; r++) {
          var label = rows[r].querySelector('.product-specs__label');
          var value = rows[r].querySelector('.product-specs__value');
          var text = (label ? label.textContent : '') + ' ' + (value ? value.textContent : '');
          index.push({ el: rows[r], hay: normalize(text) + ' ' + catLabel });
        }
      }
    }

    // Croix d'effacement : visible dès qu'il y a du texte. Le bouton est en position
    // absolue, hors flux — le révéler ne déplace rien (pas de CLS), ce qui autorise
    // l'appel au chargement quand le navigateur restaure une saisie (retour arrière).
    function syncClearButton() {
      if (!clearBtn) return;
      clearBtn.hidden = searchInput.value === '';
    }

    function updateCount(count) {
      if (!countEl) return;
      countEl.textContent = countTemplate ? countTemplate.replace('__COUNT__', count) : String(count);
      countEl.hidden = false;
    }

    function enterSearch() {
      clearTimeout(muteTimer);
      if (isSearching) return;
      isSearching = true;
      // État `open` capturé à CHAQUE entrée en recherche, pas une fois pour toutes : entre
      // deux recherches l'utilisateur a pu plier ou déplier des cartes, et c'est ce
      // dernier état qu'il doit retrouver en effaçant.
      catState = [];
      for (var i = 0; i < cats.length; i++) catState.push(cats[i].open);
      root.classList.add('is-searching');
    }

    function exitSearch() {
      if (!isSearching) return;
      isSearching = false;
      for (var i = 0; i < cats.length; i++) {
        cats[i].hidden = false;
        if (catState) cats[i].open = catState[i];
      }
      catState = null;
      // `toggle` est dispatché de façon asynchrone : retirer `is-searching` dans le même
      // tour de boucle laisserait les refermetures ci-dessus atteindre le tracker
      // d'accordéon. On la retire au tour suivant, une fois les toggles écoulés.
      clearTimeout(muteTimer);
      muteTimer = setTimeout(function () {
        root.classList.remove('is-searching');
      }, 0);
    }

    // Masque les cartes sans résultat, ouvre celles qui en portent, et marque les 1re/2e
    // lignes RÉELLEMENT visibles (les filets CSS raisonnent sinon en position DOM).
    function markCats() {
      for (var c = 0; c < cats.length; c++) {
        var list = cats[c].querySelector('.product-specs__list');
        var rows = list ? list.querySelectorAll('.product-specs__row') : [];
        var visible = 0;
        for (var r = 0; r < rows.length; r++) {
          rows[r].classList.remove(FIRST_CLASS, SECOND_CLASS);
          if (rows[r].hidden) continue;
          visible++;
          if (visible === 1) rows[r].classList.add(FIRST_CLASS);
          else if (visible === 2) rows[r].classList.add(SECOND_CLASS);
        }
        if (list) list.classList.add('is-filtered');
        cats[c].hidden = visible === 0;
        // Affectation idempotente : un `open` déjà vrai ne redéclenche aucun `toggle`.
        if (visible > 0) cats[c].open = true;
      }
    }

    function resetRows() {
      if (index) {
        for (var i = 0; i < index.length; i++) {
          index[i].el.hidden = false;
          index[i].el.classList.remove(FIRST_CLASS, SECOND_CLASS);
        }
      }
      var lists = root.querySelectorAll('.product-specs__list.is-filtered');
      for (var l = 0; l < lists.length; l++) lists[l].classList.remove('is-filtered');
    }

    // ET strict sur tous les termes, sans repli partiel (contrairement à la FAQ) : sur des
    // caractéristiques, « poids batterie » ne doit pas ramener toutes les lignes de poids
    // PLUS toutes celles de batterie — le client lit un tableau, pas une liste de réponses.
    function applyFilter(raw) {
      var terms = queryTerms(raw);
      if (!terms.length) {
        resetRows();
        exitSearch();
        lastCount = 0;
        if (countEl) countEl.hidden = true;
        if (emptyEl) emptyEl.hidden = true;
        return;
      }

      ensureIndex();
      enterSearch();

      var total = 0;
      for (var i = 0; i < index.length; i++) {
        var ok = true;
        for (var t = 0; t < terms.length; t++) {
          if (!hayMatches(index[i].hay, terms[t])) {
            ok = false;
            break;
          }
        }
        index[i].el.hidden = !ok;
        if (ok) total++;
      }
      markCats();
      lastCount = total;
      updateCount(total);
      if (emptyEl) emptyEl.hidden = total !== 0;
    }

    // --- Recherche : filtre (200 ms) + tracking (800 ms, ≥ 3 caractères) ---
    var filterTimer = null;
    var trackTimer = null;

    searchInput.addEventListener('input', function () {
      var raw = searchInput.value.trim();
      syncClearButton();

      clearTimeout(filterTimer);
      filterTimer = setTimeout(function () {
        applyFilter(raw);
      }, FILTER_DEBOUNCE_MS);

      clearTimeout(trackTimer);
      if (normalize(raw).length >= TRACK_MIN_LENGTH) {
        trackTimer = setTimeout(function () {
          pushEvent('specs_search', {
            search_term: raw,
            results_count: lastCount,
            ecommerce: productEcommerce(),
          });
        }, TRACK_DEBOUNCE_MS);
      }
    });

    // Effacement : les deux debounces en cours sont annulés d'abord. Sans le clearTimeout
    // du filtre, un applyFilter() programmé juste avant le clic se déclencherait APRÈS et
    // réafficherait les résultats d'une requête effacée.
    if (clearBtn) {
      clearBtn.addEventListener('click', function () {
        clearTimeout(filterTimer);
        clearTimeout(trackTimer);
        searchInput.value = '';
        syncClearButton();
        applyFilter('');
        searchInput.focus();
        pushEvent('click_cta', {
          // Lu sur l'attribut, source unique : c'est lui qui sort aussi le bouton du
          // tracker générique de CTAs (cf. commentaire dans custom-informations.liquid).
          cta_name: clearBtn.getAttribute('data-cta-name') || 'specs_search_clear',
          cta_location: root.getAttribute('data-cta-location') || 'product_specs',
          spec_action: 'search_clear',
          ecommerce: productEcommerce(),
        });
      });
    }

    // Changement de variante sans rechargement (`variant:change`, product-template.liquid) :
    // la ligne « Taille du cadre » est réécrite par le thème (#taille-cadre-text), l'index
    // porterait sinon l'ancienne valeur jusqu'au prochain rechargement.
    document.addEventListener('variant:change', function () {
      index = null;
      if (isSearching) applyFilter(searchInput.value.trim());
    });

    syncClearButton();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
